/**
 * ProtectedRoute Component
 * Wraps routes that require authentication or specific subscription tiers
 */
import { Redirect, useLocation } from 'wouter-preact';
import { trackEvent } from '../../modules/dataService.js';
import {
    approvalStatus,
    currentUser,
    hasActiveSubscription,
    isAuthenticated,
    isLoading,
    subscriptionStatus,
    subscriptionTier
} from '../../state/userState.js';

/**
 * Check if user has an approved and active subscription
 * Returns true for ACTIVE or TRIALING status with APPROVED approval_status
 * AND tier is not FREE (must be DEMO or PRO)
 */
function canAccessData() {
  const status = subscriptionStatus.value?.toUpperCase();
  const approval = approvalStatus.value?.toUpperCase();
  const tier = subscriptionTier.value?.toUpperCase();

  // Must be approved AND have active/trialing subscription AND not be FREE tier
  const isActive = status === 'ACTIVE' || status === 'TRIALING';
  const isApproved = approval === 'APPROVED';
  const isNotFree = tier !== 'FREE';

  console.log('[ProtectedRoute] canAccessData check:', {
    status,
    approval,
    tier,
    isActive,
    isApproved,
    isNotFree,
    result: isActive && isApproved && isNotFree
  });

  return isActive && isApproved && isNotFree;
}

/**
 * Route that requires authentication
 * @param {Object} props
 * @param {boolean} props.requireAuth - Require authentication (default: true)
 * @param {string} props.requireTier - Required subscription tier (e.g., 'pro')
 * @param {boolean} props.adminOnly - Only allow admin/superusers
 * @param {boolean} props.requireSubscription - Require active subscription to access data pages
 */
export function ProtectedRoute({
  children,
  requireAuth = true,
  requireTier = null,
  adminOnly = false,
  requireSubscription = false
}) {
  const [location] = useLocation();

  // Fast check: if not authenticated and no token, redirect immediately (no loading state)
  const hasToken = typeof window !== 'undefined' ? !!localStorage.getItem('access_token') : false;

  if (requireAuth && !hasToken && !isLoading.value) {
    console.log('[ProtectedRoute] No token - redirecting to login');
    return <Redirect to={`/login?return_url=${encodeURIComponent(location)}`} />;
  }

  // Show loading state ONLY if we have a token and are validating it
  if (isLoading.value && hasToken) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px', color: '#999' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(234, 91, 12, 0.2)',
            borderTopColor: '#ea5b0c',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p>Loading...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Check authentication after loading complete
  if (requireAuth && !isAuthenticated.value) {
    console.log('[ProtectedRoute] Not authenticated - redirecting to login');
    return <Redirect to={`/login?return_url=${encodeURIComponent(location)}`} />;
  }

  // Check admin access
  if (adminOnly) {
    const isSuperUser = !!currentUser.value?.admin_role;
    console.log('[ProtectedRoute] Admin check - user:', currentUser.value?.email, 'admin_role:', currentUser.value?.admin_role, 'result:', isSuperUser);

    if (!isSuperUser) {
      console.log('[ProtectedRoute] Access denied - not a superuser');
      return <Redirect to="/" />;
    }
  }

  // Check subscription for data pages (explorer, benchmarking, compare)
  if (requireSubscription) {
    if (!canAccessData()) {
      const status = subscriptionStatus.value?.toUpperCase();
      const approval = approvalStatus.value?.toUpperCase();

      console.log('[ProtectedRoute] No active subscription - redirecting to plans', { status, approval });

      // Track paywall hit for analytics
      trackEvent('Paywall Hit', { page: location, feature: 'data_access', status: approval || 'none' });

      // Different redirect based on status
      if (approval === 'PENDING') {
        // User has pending request - show subscription page with processing status
        return <Redirect to="/subscription" />;
      } else if (approval === 'DECLINED') {
        // User was declined - show subscription page
        return <Redirect to="/subscription?declined=true" />;
      } else {
        // No subscription yet - redirect to plan selection
        return <Redirect to="/plans" />;
      }
    }
  }

  // Check subscription tier
  if (requireTier) {
    const tierOrder = ['free', 'basic', 'pro', 'enterprise'];
    const currentTierIndex = tierOrder.indexOf(subscriptionTier.value?.toLowerCase());
    const requiredTierIndex = tierOrder.indexOf(requireTier.toLowerCase());

    if (currentTierIndex < requiredTierIndex) {
      return <Redirect to="/subscription?upgrade=true" />;
    }
  }

  // Check if subscription is active (not expired/cancelled)
  if (requireTier && requireTier !== 'free' && !hasActiveSubscription()) {
    return <Redirect to="/subscription?expired=true" />;
  }

  return children;
}

/**
 * Public-only route (redirect to home if already authenticated)
 */
export function PublicRoute({ children, redirectTo = '/' }) {
  if (isAuthenticated.value) {
    return <Redirect to={redirectTo} />;
  }

  return children;
}
